import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React from 'react';
import debounce from 'lodash/debounce';
import { Fragment } from '@atlaskit/editor-prosemirror/model';
import { convertProsemirrorNodeToMarkdownAsync } from '@atlassian/ai-model-io/convert-prosemirror-to-markdown';
import { usePublish } from '@atlassian/conversation-assistant-pubsub';
import { getPluginState } from '../../../pm-plugins/decoration/decoration-plugin-factory';
import { updatePublish } from '../../../pm-plugins/rovo-agents/commands';
import { getMentionMap } from '../../../utils/mentions/get-mention-map';

// See https://developer.mozilla.org/en-US/docs/Web/API/Scheduler/

var getScheduler = function getScheduler(obj) {
  if (!obj) {
    return null;
  }
  if ('scheduler' in obj) {
    return obj.scheduler;
  }
  return null;
};
export var getSelectionContent = function getSelectionContent(_ref) {
  var _modalDecorationSet$f, _modalDecorationSet$f2, _modalDecorationSet$f3;
  var modalDecorationSet = _ref.modalDecorationSet,
    doc = _ref.doc;
  var existingDecoration = (_modalDecorationSet$f = modalDecorationSet.find(undefined, undefined, function (spec) {
    return spec.key === 'inlineDecoration';
  })) === null || _modalDecorationSet$f === void 0 ? void 0 : _modalDecorationSet$f.shift();
  var startWidget = (_modalDecorationSet$f2 = modalDecorationSet.find(undefined, undefined, function (spec) {
    return spec.key === 'generatedStartWidget';
  })) === null || _modalDecorationSet$f2 === void 0 || (_modalDecorationSet$f2 = _modalDecorationSet$f2.shift()) === null || _modalDecorationSet$f2 === void 0 ? void 0 : _modalDecorationSet$f2.from;
  var endWidget = (_modalDecorationSet$f3 = modalDecorationSet.find(undefined, undefined, function (spec) {
    return spec.key === 'generatedEndWidget';
  })) === null || _modalDecorationSet$f3 === void 0 || (_modalDecorationSet$f3 = _modalDecorationSet$f3.shift()) === null || _modalDecorationSet$f3 === void 0 ? void 0 : _modalDecorationSet$f3.to;

  // If an AI decoration exists, use the content within the decoration range instead
  if (existingDecoration) {
    return doc.slice(existingDecoration.from, existingDecoration.to, true).content;
  }
  if (startWidget && endWidget) {
    return doc.slice(startWidget, endWidget, true).content;
  }
  return;
};
/**
 *  This component is required so we can call the usePublish hook
 *  and store the publish function in the rovo-agents plugin on mount.
 */
export var PublishToRovo = function PublishToRovo(_ref2) {
  var editorView = _ref2.editorView,
    getMentionNameDetails = _ref2.getMentionNameDetails,
    editorPluginAIProvider = _ref2.editorPluginAIProvider;
  var scheduler = getScheduler(window);
  var publish = usePublish('ai-mate');
  var product = editorPluginAIProvider.product;
  React.useEffect(function () {
    var publishDocAndSelection = /*#__PURE__*/function () {
      var _ref4 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(_ref3) {
        var doc, selection, _getPluginState, modalDecorationSet, selectionContent, selectionContentFromDecorations, mentionMap, _yield$convertProsemi, selectionMD, _yield$convertProsemi2, documentMD;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              doc = _ref3.doc, selection = _ref3.selection;
              if (!(!doc || !selection)) {
                _context.next = 4;
                break;
              }
              publish({
                type: 'editor-context-payload',
                source: 'editor',
                product: product,
                data: undefined
              });
              return _context.abrupt("return");
            case 4:
              // Retrieve the modal decoration set from the plugin state to access the selection state,
              // since the editor's selection is cleared when the modal opens.
              _getPluginState = getPluginState(editorView.state), modalDecorationSet = _getPluginState.modalDecorationSet; // Initially, assume the selected content from editorView will be used
              selectionContent = selection.content().content;
              selectionContentFromDecorations = getSelectionContent({
                modalDecorationSet: modalDecorationSet,
                doc: doc
              });
              if (selectionContentFromDecorations) {
                selectionContent = selectionContentFromDecorations;
              }
              mentionMap = {};
              if (!getMentionNameDetails) {
                _context.next = 13;
                break;
              }
              _context.next = 12;
              return getMentionMap({
                node: doc,
                getMentionNameDetails: getMentionNameDetails
              });
            case 12:
              mentionMap = _context.sent;
            case 13:
              _context.next = 15;
              return convertProsemirrorNodeToMarkdownAsync({
                node: Fragment.from(selectionContent),
                featureToggles: {
                  markdownPlus: false
                },
                mentionMap: mentionMap
              });
            case 15:
              _yield$convertProsemi = _context.sent;
              selectionMD = _yield$convertProsemi.markdown;
              _context.next = 19;
              return convertProsemirrorNodeToMarkdownAsync({
                node: doc,
                featureToggles: {
                  markdownPlus: false
                },
                mentionMap: mentionMap
              });
            case 19:
              _yield$convertProsemi2 = _context.sent;
              documentMD = _yield$convertProsemi2.markdown;
              publish({
                type: 'editor-context-payload',
                source: 'editor',
                product: product,
                data: {
                  document: {
                    type: 'text/markdown',
                    content: documentMD
                  },
                  selection: {
                    type: 'text/markdown',
                    content: selectionMD
                  }
                }
              });
            case 22:
            case "end":
              return _context.stop();
          }
        }, _callee);
      }));
      return function publishDocAndSelection(_x) {
        return _ref4.apply(this, arguments);
      };
    }();
    var debouncedPublishDocAndSelection = debounce( /*#__PURE__*/function () {
      var _ref5 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(args) {
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) switch (_context2.prev = _context2.next) {
            case 0:
              if (!scheduler) {
                _context2.next = 5;
                break;
              }
              _context2.next = 3;
              return scheduler.postTask(function () {
                return publishDocAndSelection(args);
              }, {
                priority: 'background',
                delay: 0
              });
            case 3:
              _context2.next = 7;
              break;
            case 5:
              _context2.next = 7;
              return publishDocAndSelection(args);
            case 7:
            case "end":
              return _context2.stop();
          }
        }, _callee2);
      }));
      return function (_x2) {
        return _ref5.apply(this, arguments);
      };
    }(), 100);

    // Initial publish on Editor init
    debouncedPublishDocAndSelection({
      doc: editorView.state.doc,
      selection: editorView.state.selection
    });

    // Save the debounced function to the rovo-agents plugin state
    updatePublish(debouncedPublishDocAndSelection, publish)(editorView.state, editorView.dispatch);
  }, [publish, editorView, getMentionNameDetails, product, scheduler]);
  return /*#__PURE__*/React.createElement(React.Fragment, null);
};